"use client";

import React, { useEffect, useState } from "react";
import useTemplateBase from "./TemplateBase";
import { Button } from "@/components/ui/button";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Check,
  Tag,
  X,
  Loader2,
  Plus,
  ImageIcon,
  Home,
  Building2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import profile from "../../../public/profilePicture/profile.webp";
import openHouseBadge from "../../assets/star_red.png";

interface LoggedUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  image?: string;
  avatar?: string;
  company?: string;
  company_logo?: string;
}

interface Property {
  id: number;
  propertyListType?: { id: number; list_name: string; custom_template_data?: Record<string, any> };
  title: string;
  listing_id?: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  photos?: string[] | null;
  is_open_house?: boolean;
}

interface PropertyList {
  id: number;
  list_name: string;
}

interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

interface Template9Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Partial<any>) => void;
  onPropertyListChange?: (selectedList: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  selectedList?: string; // ✅ Already exists
  readOnly?: boolean; // ✅ Already exists
}

interface FeatureItem {
  id?: number;
  feature_name: string;
}

const Template9: React.FC<Template9Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  selectedList: viewSelectedList, // ✅ Already exists
  onPropertyListCreated,
  readOnly = false, // ✅ Already exists
}) => {
  const { toast } = useToast();

  const [isCustomList, setIsCustomList] = useState(false);
  const [customListName, setCustomListName] = useState("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] = useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<string | null>(null);

  // Room labels state
  const [roomLabels, setRoomLabels] = useState<string[]>(["KITCHEN", "LIVING ROOM", "BEDROOM"]);

  // Image refs for thumbnails
  const thumbnailRefs = [
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
    React.useRef<HTMLInputElement>(null),
  ];

  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePropertyList,
    updatePhotoAtIndex,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  useEffect(() => {
    setLocalSelectedListOverride(null);
  }, [property?.id]);

  const handleRoomLabelChange = (index: number, value: string) => {
    if (readOnly) return;
    setRoomLabels((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });
    onDirtyChange?.(true);
  };

  // ✅ Handle main image change - added readOnly check
  const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return; // ✅ ADD THIS

    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Image must be less than 10 MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setFlyerData((prev) => ({ ...prev, heroImage: result }));
      onDirtyChange?.(true);
      onChange?.({ heroImage: result });
    };
    reader.readAsDataURL(file);
  };

  // ✅ Handle thumbnail image upload - added readOnly check
  const handleThumbnailChange = async (index: number, e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return; // ✅ ADD THIS

    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image file",
        variant: "destructive"
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Image must be less than 10 MB",
        variant: "destructive",
      });
      return;
    }

    const slotName = index === 0 ? "photo2" : index === 1 ? "photo3" : "photo4";

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      setFlyerData((prev) => ({ ...prev, [slotName]: dataUrl }));

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        const uploadRes = await axiosInstance.post(endpoints.mls_uploads, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "multipart/form-data",
          },
        });

        if (uploadRes?.data?.url) {
          await updatePhotoAtIndex(uploadRes.data.url, slotName as "photo2" | "photo3" | "photo4");
          toast({ title: "Success", description: "Photo uploaded successfully", duration: 1000 });
        }
      } catch (err) {
        console.error("Failed to upload thumbnail:", err);
        toast({
          title: "Error",
          description: "Failed to upload photo",
          variant: "destructive"
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // ✅ Determine currently selected list - already includes viewSelectedList
  const selectedList =
    localSelectedListOverride ??
    viewSelectedList ?? // ✅ Already included
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "Elegant";

  const allLists = localPropertyLists.length > 0
    ? localPropertyLists
    : [
      { id: 1, list_name: "Elegant" },
      { id: 2, list_name: "Luxury" },
      { id: 3, list_name: "Modern" },
      { id: 4, list_name: "Classic" },
    ];

  // ✅ Save custom list - added readOnly check
  const saveCustomList = async () => {
    if (readOnly) return; // ✅ ADD THIS

    const trimmedName = customListName.trim();

    if (!trimmedName) {
      toast({
        title: "Error",
        description: "Please enter a name for your custom tag",
        variant: "destructive"
      });
      return;
    }

    if (!property?.id) {
      toast({
        title: "Error",
        description: "No property selected",
        variant: "destructive"
      });
      return;
    }

    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");
      toast({
        title: "List Selected",
        description: `"${existingList.list_name}" selected`
      });
      return;
    }

    setIsSavingCustomList(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );

      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };

      setLocalSelectedListOverride(newListType.list_name);
      setLocalPropertyLists((prev) => [...prev, newListType]);
      setIsCustomList(false);
      setCustomListName("");
      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);

      toast({
        title: "Custom Tag Created",
        description: `"${newListType.list_name}" created`
      });
    } catch (err: any) {
      console.error("Failed to create custom list:", err);
      toast({
        title: "Error",
        description: "Failed to create custom tag",
        variant: "destructive"
      });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  // ✅ Handle list selection - added readOnly check
  const handleListSelection = (value: string) => {
    if (readOnly) return; // ✅ ADD THIS

    if (value === "__custom__") {
      setIsCustomList(true);
      setCustomListName("");
      return;
    }

    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  // Open House state
  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  const images = [
    flyerData.heroImage || "",
    flyerData.photo2 || "",
    flyerData.photo3 || "",
    flyerData.photo4 || "",
  ];

  return (
    <div className="bg-white overflow-hidden w-full border border-gray-200 rounded-xl shadow-none font-arial">

      {/* Header with Company Branding */}
      <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-r from-[#7A0F2E] to-[#C2185B]">
        <div className="flex items-center gap-2">
          <div className="w-0 h-0 border-l-[8px] border-l-transparent border-r-[8px] border-r-transparent border-b-[14px] border-b-[#E6B8A2]" />
          <span className="text-[#E6B8A2] text-xs font-semibold uppercase tracking-wider">
            {agentData?.company || "Premier Properties"}
          </span>
        </div>

        {/* Tag Selector */}
        <div className="flex items-center gap-2">
          <Tag className="h-3 w-3 text-white" />
          {/* ✅ ADD READONLY CHECK for tag selector display */}
          {readOnly ? (
            <span className="bg-white/20 text-white px-3 py-1 rounded text-xs font-semibold">
              {selectedList || property?.propertyListType?.list_name || "Elegant"}
            </span>
          ) : isCustomList ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={customListName}
                onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                placeholder="Custom tag (max 20 chars)"
                maxLength={20}
                className="bg-white/20 text-white placeholder-white/60 px-2 py-1 rounded text-xs w-24 outline-none border border-white/30"
                autoFocus
                disabled={isSavingCustomList}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !isSavingCustomList) {
                    e.preventDefault();
                    saveCustomList();
                  }
                  if (e.key === "Escape") cancelCustomList();
                }}
              />
              <button
                onClick={saveCustomList}
                disabled={!customListName.trim() || isSavingCustomList}
                className="p-1 bg-white/20 rounded hover:bg-white/30 disabled:opacity-50"
              >
                {isSavingCustomList ? (
                  <Loader2 className="h-3 w-3 text-white animate-spin" />
                ) : (
                  <Check className="h-3 w-3 text-white" />
                )}
              </button>
              <button
                onClick={cancelCustomList}
                disabled={isSavingCustomList}
                className="p-1 bg-white/20 rounded hover:bg-white/30"
              >
                <X className="h-3 w-3 text-white" />
              </button>
            </div>
          ) : (
            <select
              value={selectedList}
              onChange={(e) => handleListSelection(e.target.value)}
              className="bg-white/20 text-white px-3 py-1 rounded text-xs font-semibold border-none outline-none cursor-pointer"
            >
              {allLists.map((list) => (
                <option key={list.id} value={list.list_name} className="text-gray-900 bg-white">
                  {list.list_name}
                </option>
              ))}
              <option value="__custom__" className="text-gray-900 bg-white">
                + Add Custom
              </option>
            </select>
          )}
        </div>
      </div>

      {/* Hero Image Section */}
      <div className="relative">
        <div className="h-72 overflow-hidden relative group">
          {isOpenHouseConfirmed && (
            <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20">
              <div className="relative w-24 md:w-32">
                <img
                  src={openHouseBadge}
                  alt="Open House"
                  className="w-full drop-shadow-xl"
                />
                <span
                  className="absolute inset-0 flex items-center justify-center
                   text-white font-extrabold text-xs md:text-sm
                   tracking-wide text-center pointer-events-none"
                >
                  OPEN
                  <br />
                  HOUSE
                </span>
              </div>
            </div>
          )}
          {flyerData.heroImage ? (
            <img
              src={flyerData.heroImage}
              className="w-full h-full object-cover"
              alt="Property"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex flex-col items-center justify-center">
              <ImageIcon className="h-16 w-16 text-gray-400 mb-2" />
              <p className="text-sm text-gray-500">
                {readOnly ? "No image" : "Click to upload main photo"}
              </p>
            </div>
          )}

          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#7A0F2E]/60" />
          {/* Property Info Overlay */}
          {/* Property Info Overlay — AFTER */}
          <div className="absolute bottom-4 left-4 right-4 text-white z-10 overflow-hidden">
            {readOnly ? (
              <p
                className="text-lg sm:text-2xl font-bold uppercase text-white mb-1 truncate"
                title={flyerData.headline || "LUXURY HOME"}
              >
                {flyerData.headline || "LUXURY HOME"}
              </p>
            ) : (
              <input
                type="text"
                value={flyerData.headline || ""}
                onChange={(e) => handleInputChange("headline", e.target.value)}
                className="text-lg sm:text-2xl font-bold uppercase bg-transparent outline-none text-white block mb-1 w-full truncate"
                placeholder="LUXURY HOME"
              />
            )}
            <span className="text-lg sm:text-2xl font-bold text-[#E6B8A2] uppercase block">
              For Sale
            </span>
          </div>

          {/* Upload Button - ✅ Only show when NOT readOnly */}
          {!readOnly && (
            <Button
              size="sm"
              variant="outline"
              className="absolute top-2 right-2 bg-white/90 hover:bg-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-3 w-3 mr-1" />
              Change
            </Button>
          )}
          {/* ✅ Only render file input when NOT readOnly */}
          {!readOnly && (
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleMainImageChange}
            />
          )}
        </div>
      </div>

      {/* Thumbnail Gallery - Fixed Proper Sizing with Room Labels */}
      <div className="bg-[#FFF7F9] px-4 py-4 border-t border-[#C2185B]/10">
        <div className="grid grid-cols-3 gap-3 max-w-2xl mx-auto">
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className={`relative aspect-[4/3] rounded-lg overflow-hidden bg-gray-200 border-2 border-white shadow-lg group ${!readOnly ? 'cursor-pointer' : ''} hover:shadow-xl transition-all duration-300`}
              onClick={() => !readOnly && thumbnailRefs[index].current?.click()}
            >
              {images[index + 1] ? (
                <img
                  src={images[index + 1]}
                  className="w-full h-full object-cover"
                  alt={`Room ${index + 1}`}
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
                  <ImageIcon className="h-8 w-8 text-gray-400 mb-1" />
                  <span className="text-xs text-gray-500">
                    {readOnly ? "No photo" : "Photo"}
                  </span>
                </div>
              )}

              {/* Upload Overlay on Hover - ✅ Only show when NOT readOnly */}
              {!readOnly && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center">
                  <Upload className="h-6 w-6 text-white mb-1" />
                  <span className="text-xs text-white font-medium">
                    {images[index + 1] ? "Change" : "Upload"}
                  </span>
                </div>
              )}

              {/* Room Label Badge */}
              {/* <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#7A0F2E]/80 to-transparent p-2"> */}
              <div className="absolute bottom-0 left-0 right-0 p-2">

                {/* ✅ Room Label - readOnly conditional */}
                {readOnly ? (
                  <p className="text-white text-xs font-semibold text-center uppercase tracking-wide">
                    {/* {roomLabels[index] || "ROOM NAME"} */}
                  </p>
                ) : (
                  <input
                    type="text"
                    value={roomLabels[index]}
                    onChange={(e) => handleRoomLabelChange(index, e.target.value.toUpperCase())}
                    onClick={(e) => e.stopPropagation()}
                    className="text-white text-xs font-semibold bg-transparent border-none outline-none w-full text-center uppercase tracking-wide"
                    placeholder="ROOM NAME"
                  />
                )}
              </div>

              {/* ✅ Only render file input when NOT readOnly */}
              {!readOnly && (
                <input
                  ref={thumbnailRefs[index]}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleThumbnailChange(index, e)}
                />
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Content Section */}
      <div className="bg-gradient-to-b from-[#FFF7F9] to-white pt-6 pb-6 px-5">
        {/* Property Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="text-center bg-white rounded-xl p-4 shadow-sm border border-[#C2185B]/10">
            <Bed className="h-6 w-6 text-[#C2185B] mx-auto mb-2" />
            {/* ✅ Bedrooms - readOnly conditional */}
            {readOnly ? (
              <p className="text-2xl font-bold text-gray-900 text-center mb-1">
                {flyerData.bedrooms ?? 0}
              </p>
            ) : (
              <input
                type="number"
                min="0"
                value={flyerData.bedrooms ?? ""}
                onChange={(e) => handleInputChange("bedrooms", e.target.value ? parseInt(e.target.value) : null)}
                className="text-2xl font-bold text-gray-900 w-full text-center bg-transparent border-none outline-none mb-1"
                placeholder="0"
              />
            )}
            <p className="text-xs text-gray-600 uppercase font-medium">Beds</p>
          </div>
          <div className="text-center bg-white rounded-xl p-4 shadow-sm border border-[#C2185B]/10">
            <Bath className="h-6 w-6 text-[#C2185B] mx-auto mb-2" />
            {/* ✅ Bathrooms - readOnly conditional */}
            {readOnly ? (
              <p className="text-2xl font-bold text-gray-900 text-center mb-1">
                {flyerData.bathrooms ?? 0}
              </p>
            ) : (
              <input
                type="number"
                value={flyerData.bathrooms ?? ""}
                onChange={(e) => handleInputChange("bathrooms", e.target.value ? parseInt(e.target.value) : null)}
                className="text-2xl font-bold text-gray-900 w-full text-center bg-transparent border-none outline-none mb-1"
                placeholder="0"
              />
            )}
            <p className="text-xs text-gray-600 uppercase font-medium">Baths</p>
          </div>
          <div className="text-center bg-white rounded-xl p-4 shadow-sm border border-[#C2185B]/10">
            <Square className="h-6 w-6 text-[#C2185B] mx-auto mb-2" />
            {/* ✅ Sqft - readOnly conditional */}
            {readOnly ? (
              <p className="text-2xl font-bold text-gray-900 text-center mb-1">
                {flyerData.sqft ?? flyerData.area ?? 0}
              </p>
            ) : (
              <input
                type="number"
                value={flyerData.sqft ?? flyerData.area ?? ""}
                onChange={(e) => handleInputChange("sqft", e.target.value ? parseInt(e.target.value) : null)}
                className="text-2xl font-bold text-gray-900 w-full text-center bg-transparent border-none outline-none mb-1"
                placeholder="0"
              />
            )}
            <p className="text-xs text-gray-600 uppercase font-medium">Sq Ft</p>
          </div>
        </div>

        {/* Features Section */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-center w-full">
              <span className="font-bold text-xl text-gray-900">HOME </span>
              <span className="font-bold text-xl text-[#C2185B]">FEATURES</span>
            </div>
          </div>
          <div className="w-16 h-1 bg-[#C2185B] mx-auto mb-4 rounded-full" />

          {/* <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700 uppercase">Property Features</h3>
            {!readOnly && (
              <span className="text-xs text-gray-500">
                {flyerData.features?.length || 0}/10
              </span>
            )}
          </div> */}

          <div className="space-y-3 mb-4">
            {(flyerData.features || []).map((feature: FeatureItem, index: number) => (
              <div
                key={feature.id ?? index}
                className="flex items-center gap-3 bg-gradient-to-r from-[#C2185B]/5 to-transparent rounded-lg p-3 group hover:from-[#C2185B]/10 transition-colors border border-[#C2185B]/10"
              >
                <div className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0 bg-[#C2185B]">
                  <Check className="h-4 w-4 text-white" strokeWidth={3} />
                </div>
                {/* ✅ Features - readOnly conditional */}
                {readOnly ? (
                  <span className="text-sm text-gray-800 font-medium">
                    {feature.feature_name || "Feature"}
                  </span>
                ) : (
                  <>
                    <input
                      type="text"
                      value={feature.feature_name}
                      onChange={(e) => handleFeatureChange(index, e.target.value)}
                      onBlur={() => handleFeatureBlur(index)}
                      maxLength={30}
                      placeholder="Hardwood Floors (max 30 chars)..."
                      className="bg-transparent border-none outline-none text-sm text-gray-800 flex-1 font-medium"
                    />
                    <button
                      onClick={() => handleFeatureDelete(index)}
                      className="opacity-0 group-hover:opacity-100 text-red-500 hover:text-red-700 transition-opacity"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            ))}
          </div>

          {/* ✅ Add Feature Button - Only show when NOT readOnly */}
          {!readOnly && (flyerData.features?.length || 0) < 10 && (
            <Button
              size="sm"
              variant="outline"
              onClick={addFeature}
              className="w-full text-xs border-dashed border-[#C2185B] text-[#C2185B] hover:bg-[#C2185B]/5"
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Feature
            </Button>
          )}
        </div>

        {/* Price Card */}
        <div className="bg-gradient-to-r from-[#7A0F2E] to-[#C2185B] text-white rounded-xl px-6 py-5 shadow-lg mb-6">
          <p className="text-xs uppercase tracking-wider mb-2 text-[#E6B8A2] font-semibold text-center">
            Price
          </p>
          {/* ✅ Price - readOnly conditional */}
          {readOnly ? (
            <p className="text-3xl font-bold text-center text-white">
              {flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : "$0"}
            </p>
          ) : (
            <input
              type="text"
              value={flyerData.price ? `$${Number(flyerData.price).toLocaleString()}` : ""}
              onChange={(e) => {
                const num = e.target.value.replace(/[^0-9]/g, "");
                handleInputChange("price", num === "" ? null : Number(num));
              }}
              className="text-3xl font-bold bg-transparent outline-none w-full text-center"
              placeholder="$725,000"
            />
          )}
        </div>

        {/* Description */}
        <div className="mb-6">
          {/* ✅ Description - readOnly conditional */}
          {readOnly ? (
            <div className="w-full bg-white border border-gray-200 rounded-lg p-4 text-sm text-gray-700 leading-relaxed whitespace-pre-line break-words">
              {flyerData.description || "No description provided."}
            </div>
          ) : (
            <textarea
              value={flyerData.description || ""}
              onChange={(e) => handleInputChange("description", e.target.value)}
              className="w-full bg-white border border-gray-200 rounded-lg p-4 outline-none resize-none text-sm text-gray-700 leading-relaxed"
              rows={3}
              placeholder="Property description..."
            />
          )}
        </div>

        {/* Agent Footer */}
        <div className="bg-white rounded-xl px-6 py-4 shadow-sm border border-gray-100 mb-4">
          <div className="flex items-center gap-4 w-full">
            {/* Avatar */}
            <div className="w-14 h-14 rounded-full border border-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              <img
                src={agentData?.avatar || profile}
                alt="Agent"
                className="w-full h-full object-cover"
              />
            </div>

            {/* Company Logo */}
            <div className="w-14 h-14 rounded-full border border-gray-200 bg-white flex items-center justify-center text-xs text-gray-400 flex-shrink-0 overflow-hidden">
              {agentData?.company_logo ? (
                <img
                  src={agentData.company_logo}
                  alt="Logo"
                  className="w-full h-full object-cover rounded-full"
                />
              ) : (
                <Building2 className="h-6 w-6 text-gray-300" />
              )}
            </div>

            {/* Text Section */}
            <div className="flex flex-col justify-center min-w-0 flex-1 overflow-hidden">

              {/* Agent Footer */}
              {readOnly ? (
                <h2 className="text-base font-semibold text-[#C2185B] leading-tight mb-1 truncate w-full"
                  title={flyerData.agentName || "Agent Name"}>
                  {flyerData.agentName || "Agent Name"}
                </h2>
              ) : (
                <input
                  type="text"
                  value={flyerData.agentName || ""}
                  onChange={(e) => handleInputChange("agentName", e.target.value)}
                  className="bg-transparent text-base font-semibold text-[#C2185B] outline-none mb-1 truncate w-full"
                  placeholder="Agent Name"
                />
              )}

              {/* Phone */}
              <div className="flex items-center gap-2 mb-1.5 min-w-0 overflow-hidden">
                <Phone className="h-3.5 w-3.5 text-[#C2185B] flex-shrink-0" />
                {readOnly ? (
                  <span className="text-xs text-gray-700 truncate min-w-0 flex-1"
                    title={flyerData.agentPhone || "N/A"}>
                    {flyerData.agentPhone || "N/A"}
                  </span>
                ) : (
                  <input type="text" value={flyerData.agentPhone || ""}
                    onChange={(e) => handleInputChange("agentPhone", e.target.value)}
                    className="bg-transparent text-xs text-gray-700 outline-none min-w-0 flex-1 truncate"
                    placeholder="Phone" />
                )}
              </div>

              {/* Company */}
              <div className="flex items-center gap-2 mb-1.5 min-w-0 overflow-hidden">
                <Building2 className="h-3.5 w-3.5 text-[#C2185B] flex-shrink-0" />
                {readOnly ? (
                  <span className="text-xs text-gray-700 truncate min-w-0 flex-1"
                    title={flyerData.company || agentData?.company || "N/A"}>
                    {flyerData.company || agentData?.company || "N/A"}
                  </span>
                ) : (
                  <input type="text" value={flyerData.company || agentData?.company || ""}
                    onChange={(e) => handleInputChange("company", e.target.value)}
                    className="bg-transparent text-xs text-gray-700 outline-none min-w-0 flex-1 truncate"
                    placeholder="Company" />
                )}
              </div>
              {/* Email */}
              <div className="flex items-center gap-2 min-w-0 overflow-hidden">
                <Mail className="h-3.5 w-3.5 text-[#C2185B] flex-shrink-0" />
                {readOnly ? (
                  <span className="text-xs text-gray-700 truncate min-w-0 flex-1"
                    title={flyerData.agentEmail || "N/A"}>
                    {flyerData.agentEmail || "N/A"}
                  </span>
                ) : (
                  <input type="text" value={flyerData.agentEmail || ""}
                    onChange={(e) => handleInputChange("agentEmail", e.target.value)}
                    className="bg-transparent text-xs text-gray-700 outline-none min-w-0 flex-1 truncate"
                    placeholder="Email" />
                )}
              </div>
            </div>

          </div>
        </div>





      </div>

      {/* Bottom Accent Bar */}
      <div className="h-3 bg-gradient-to-r from-[#7A0F2E] to-[#C2185B]" />
    </div>
  );
};

export default Template9;
